import { useState, useMemo, useCallback } from 'react'

import type { Review, RatingBreakdown } from '../types'

export interface RatingStats {
  reviews: Review[]
  percentage: number
  voteCount: number
}

export interface ReviewsActionsValue {
  /** Average rating (0-5) */
  averageRating: number
  /** Total number of reviews */
  reviewCount: number
  /** Get stats for a specific rating (1-5) */
  getStatsByRating: (rating: number) => RatingStats
  /** Stats for each rating level */
  ratingStats: {
    five: RatingStats
    four: RatingStats
    three: RatingStats
    two: RatingStats
    one: RatingStats
  }
  /** Whether review list is expanded */
  isExpanded: boolean
  /** Toggle expand/collapse and scroll to reviews */
  toggleExpand: () => void
  /** Navigate to review form */
  navigateToReviewForm: () => void
}

/**
 * Hook for review business logic and calculations
 *
 * Extracts all calculation and navigation logic from components.
 * Components should only render UI.
 */
export function useReviewsActions(reviews: Review[]): ReviewsActionsValue {
  const reviewCount = reviews.length
  const [isExpanded, setIsExpanded] = useState(false)

  /**
   * Calculate stats for a specific rating (1-5)
   */
  const getStatsByRating = useCallback(
    (rating: number): RatingStats => {
      if (reviewCount === 0) {
        return { reviews: [], percentage: 0, voteCount: 0 }
      }

      let voteCount = 0

      const filteredReviews = reviews.filter((review: Review) => {
        const ratingVotes = review.ratings_breakdown.filter(
          (vote: RatingBreakdown) => Number(vote.value) === rating
        )
        voteCount += ratingVotes.length
        return ratingVotes.length > 0
      })

      const percentage = Math.round((filteredReviews.length / reviewCount) * 100)

      return {
        reviews: filteredReviews,
        percentage,
        voteCount,
      }
    },
    [reviews, reviewCount]
  )

  /**
   * Calculate average rating across all reviews
   */
  const averageRating = useMemo(() => {
    if (reviewCount === 0) return 0

    const totalStarsValue = reviews.reduce((sum, review) => {
      const reviewStars =
        review.ratings_breakdown.reduce((rSum, vote) => rSum + Number(vote.value), 0) /
        review.ratings_breakdown.length
      return sum + reviewStars
    }, 0)

    return Number((totalStarsValue / reviewCount).toFixed(1))
  }, [reviews, reviewCount])

  /**
   * Pre-calculated stats for each rating level
   */
  const ratingStats = useMemo(
    () => ({
      five: getStatsByRating(5),
      four: getStatsByRating(4),
      three: getStatsByRating(3),
      two: getStatsByRating(2),
      one: getStatsByRating(1),
    }),
    [getStatsByRating]
  )

  /**
   * Navigate to review form
   */
  const navigateToReviewForm = useCallback(() => {
    document.getElementById('review-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [])

  /**
   * Toggle expand/collapse and scroll to reviews section
   */
  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev)
    const element = document.getElementById('react-reviews')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  return {
    averageRating,
    reviewCount,
    getStatsByRating,
    ratingStats,
    isExpanded,
    toggleExpand,
    navigateToReviewForm,
  }
}
